import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DigiResponse } from '@core/models/response/core';
import { ChildDeactivationRequest } from '@core/models/domain/child-deactivation.domain';
import {
  ApproveChildDeactivationRequest,
  DeclineChildDeactivationRequest,
  ExemptChildDeactivationRequest
} from '@core/models/request/child-deactivation.request';
import { buildApiEndpoint, handleError } from '@core/utils/service';

/**
 * ChildDeactivationService
 *
 * Backend calls for the school admin "Account deactivation requests" page: parents asking to
 * deactivate their child's LOQT account, which a school-linked pupil cannot have done without the
 * school agreeing. The school is taken from the caller's token by the backend, never sent from here.
 */
@Injectable({
  providedIn: 'root'
})
export class ChildDeactivationService {
  API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService) {
    this.API_URI = this.apiService.getApiURL("UserService", "school-account");
  }

  loadPendingRequests(): Observable<ChildDeactivationRequest[]> {
    return this.loadList("child-deactivation-requests", "Loading account deactivation requests");
  }

  /** Resolved requests, kept available for audit. */
  loadResolvedRequests(): Observable<ChildDeactivationRequest[]> {
    return this.loadList("child-deactivation-requests/history", "Loading resolved deactivation requests");
  }

  approve(request: ApproveChildDeactivationRequest): Observable<string> {
    return this.decide("child-deactivation-requests/approve", request, request.requestId, "Approving the deactivation");
  }

  decline(request: DeclineChildDeactivationRequest): Observable<string> {
    return this.decide("child-deactivation-requests/decline", request, request.requestId, "Declining the request");
  }

  exempt(request: ExemptChildDeactivationRequest): Observable<string> {
    return this.decide("child-deactivation-requests/exempt", request, request.requestId, "Marking the pupil exempt");
  }

  private loadList(endpoint: string, operation: string): Observable<ChildDeactivationRequest[]> {
    return this.http
      .get<DigiResponse<ChildDeactivationRequest[]>>(buildApiEndpoint(this.API_URI, endpoint))
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error(operation + " failed");
          }
          return Array.isArray(response.body) ? response.body : [];
        }),
        catchError((error) => handleError<ChildDeactivationRequest[]>(operation, error))
      );
  }

  /** Returns the request id on success, so the reducer can take the handled row out of the list. */
  private decide(endpoint: string, body: unknown, requestId: string, operation: string): Observable<string> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.API_URI, endpoint), body)
      .pipe(
        map((response) => {
          if (response?.statusCode?.status !== "OK") {
            throw response?.statusCode ?? new Error(operation + " failed");
          }
          return requestId;
        }),
        catchError((error) => handleError<string>(operation, error))
      );
  }
}
